/**
 * NewSaleDialog - Main dialog for creating and editing sales
 * 
 * TODO: REFACTORING NEEDED (Code Quality Review)
 * This component has grown to 1,700+ lines and should be split into:
 * - ProductSearch: Product search and barcode scanning
 * - CartManager: Line items, quantities, and discounts
 * - CustomerSelector: Customer selection and creation
 * - PaymentProcessor: Payment mode selection and processing
 * - BillCalculator: Bill totals, discounts, and flat total calculations
 * 
 * For now, the code is organized into logical sections with clear comments.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Plus, User as UserIcon, Phone, UserPlus, Camera, ToggleLeft, ToggleRight, Lock, AlertTriangle } from "lucide-react";
import BarcodeScannerDialog from "./BarcodeScannerDialog";
import { AddCustomerInline, CameraScanner } from "@/pages/sales";
import { PaymentSection, BillDiscountNotes, BillSummaryBar, ManualItemDialog, BatchSelectionDialog, EditPaymentAdjustmentDialog, PaymentSummaryDialog } from "@/pages/sale";

// =====================================================================
// CONSTANTS
// =====================================================================
const DEFAULT_DISCOUNT = 5;
const PAY_MODES = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "credit", label: "Credit" },
];

// =====================================================================
// UTILITY FUNCTIONS
// =====================================================================
function r2(v) {
  return Math.round(Number(v || 0) * 100) / 100;
}

// =====================================================================
// MAIN COMPONENT
// =====================================================================
export default function NewSaleDialog({ open, onOpenChange, shop, onDone, editSale = null, editReason = "" }) {
  const isEditMode = !!editSale;
  
  // --- STATE: Sale Type & Line Items ---
  const [type, setType] = useState("retail");
  const [lines, setLines] = useState([]);
  
  // --- STATE: Discounts & Totals ---
  const [billDiscountPct, setBillDiscountPct] = useState(0);
  const [flatTotal, setFlatTotal] = useState(0); // User can enter desired final total
  
  // Partial payments
  const [cashAmount, setCashAmount] = useState(0);
  const [upiAmount, setUpiAmount] = useState(0);
  const [cardAmount, setCardAmount] = useState(0);
  const [creditAmount, setCreditAmount] = useState(0);
  const [storeCreditAmount, setStoreCreditAmount] = useState(0);
  const [availableStoreCredit, setAvailableStoreCredit] = useState(0);
  const [originalStoreCreditInSale, setOriginalStoreCreditInSale] = useState(0); // Track original SC from editSale for edit mode
  const [storeExcessAsCredit, setStoreExcessAsCredit] = useState(false); // Store excess payment as customer credit
  
  // Outstanding dues collection
  const [outstandingDues, setOutstandingDues] = useState(0);
  const [duesCollectionAmount, setDuesCollectionAmount] = useState(0);
  const [duesCollectionMode, setDuesCollectionMode] = useState("cash");
  
  const [customer, setCustomer] = useState(null); // {id,name,type,...}
  const [customerName, setCustomerName] = useState("");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualItemOpen, setManualItemOpen] = useState(false);

  // Refs for keyboard navigation
  const lineRefs = useRef({});
  const qtyInputRefs = useRef({});
  const searchInputRef = useRef(null);

  // customer picker
  const [custQ, setCustQ] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [custFocus, setCustFocus] = useState(false);
  const [addCustOpen, setAddCustOpen] = useState(false);
  const [custStats, setCustStats] = useState(null);
  const custDebRef = useRef(null);

  // search
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  
  // Stock availability for products in cart
  const [stockMap, setStockMap] = useState({}); // { product_id: quantity | null }
  const [stockBatches, setStockBatches] = useState({}); // { product_id: { batches: [], total: number } }
  
  // Stock Lock feature
  const [stockLockEnabled, setStockLockEnabled] = useState(false);
  const [searchStockMap, setSearchStockMap] = useState({}); // Stock availability for search results
  
  // Batch Selection Dialog state - for mandatory batch selection
  const [batchSelectOpen, setBatchSelectOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingBatches, setPendingBatches] = useState([]);

  // ===== Edit Mode: Payment Adjustment =====
  const [originalPaidAmount, setOriginalPaidAmount] = useState(0); // Track original amount_paid for edit mode
  const [originalSaleTotal, setOriginalSaleTotal] = useState(0); // Track original total for edit mode
  const [paymentAdjustmentOpen, setPaymentAdjustmentOpen] = useState(false);
  const [pendingEditPayload, setPendingEditPayload] = useState(null); // Store payload while adjustment dialog is open

  // ===== UX SAFETY: Close confirmation =====
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  
  // ===== TWO-STEP POS CHECKOUT: Payment Summary Dialog (New Sales Only) =====
  const [paymentSummaryOpen, setPaymentSummaryOpen] = useState(false);

  // Check if sale has meaningful data (for confirmation prompt)
  const hasSaleData = useCallback(() => {
    return lines.length > 0 || customer !== null || notes.trim() !== "" || 
           cashAmount > 0 || upiAmount > 0 || cardAmount > 0 || creditAmount > 0;
  }, [lines, customer, notes, cashAmount, upiAmount, cardAmount, creditAmount]);

  // Get total items count for display
  const totalItemsCount = useMemo(() => {
    return lines.reduce((sum, l) => sum + (l.quantity || 1), 0);
  }, [lines]);

  // Handle close with confirmation
  const handleRequestClose = useCallback(() => {
    if (hasSaleData()) {
      setShowCloseConfirm(true);
    } else {
      onOpenChange(false);
    }
  }, [hasSaleData, onOpenChange]);

  // Confirm close (discard sale)
  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    onOpenChange(false);
  }, [onOpenChange]);

  // Cancel close (continue editing)
  const cancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    
    // Fetch shop's stock_lock status
    if (shop?.id) {
      api.get(`/shops/${shop.id}`)
        .then(r => {
          setStockLockEnabled(r.data.stock_lock || false);
        })
        .catch(() => setStockLockEnabled(false));
    } else {
      setStockLockEnabled(false);
    }
    
    // If in edit mode, pre-populate form with existing sale data
    if (editSale) {
      setType(editSale.type || "retail");
      setLines((editSale.line_items || []).map((l) => ({
        product_id: l.product_id,
        product_snapshot: l.product_snapshot || {},
        mrp: Number(l.mrp || 0),
        quantity: Number(l.quantity || 1),
        unit_price: Number(l.unit_price || 0),
        line_discount_pct: Number(l.line_discount_pct || 0),
        price_type: l.price_type || "retail",
        batch_id: l.batch_id || null,
        // Preserve manual item markers across edit
        manual: !!l.manual,
        manual_name: l.manual_name || null,
        manual_type: l.manual_type || null,
        local_id: l.manual ? (l.local_id || `manual-${Math.random().toString(36).slice(2, 10)}`) : null,
        // Mark as original item from the sale - cannot be deleted, use Sales Return instead
        isOriginal: true,
      })));
      setBillDiscountPct(Number(editSale.bill_discount_pct || 0));
      // In edit mode, if there was a round_off_amount, calculate what flat total it represents
      const savedRoundOff = Number(editSale.round_off_amount || 0);
      if (savedRoundOff !== 0) {
        // We'll need to calculate the flat total from the saved data
        // flat_total = total (which already includes round_off)
        setFlatTotal(Number(editSale.total || 0));
      } else {
        setFlatTotal(0);
      }
      
      // Pre-populate payments
      const payments = editSale.payments || [];
      setCashAmount(payments.find(p => p.mode === 'cash')?.amount || 0);
      setUpiAmount(payments.find(p => p.mode === 'upi')?.amount || 0);
      setCardAmount(payments.find(p => p.mode === 'card')?.amount || 0);
      setCreditAmount(payments.find(p => p.mode === 'credit')?.amount || 0);
      // BUG FIX: Also extract store_credit payment for edit mode
      const originalSC = payments.find(p => p.mode === 'store_credit')?.amount || 0;
      setStoreCreditAmount(originalSC);
      setOriginalStoreCreditInSale(originalSC); // Track original for max calculation
      
      // Track original paid amount for payment adjustment during edit
      const origPaid = Number(editSale.amount_paid || 0);
      setOriginalPaidAmount(origPaid);
      setOriginalSaleTotal(Number(editSale.total || 0));
      
      // Pre-populate customer
      if (editSale.customer_id) {
        setCustomer({
          id: editSale.customer_id,
          name: editSale.customer_name || "",
          ...editSale.customer_snapshot,
        });
        setCustQ(editSale.customer_name || "");
      } else {
        setCustomer(null);
        setCustomerName(editSale.customer_name || "");
        setCustQ("");
      }
      
      setNotes(editSale.notes || "");
      setReason(editReason || "");
      setQ("");
      setResults([]);
      setScannerOpen(false);
      setSearchStockMap({});
    } else {
      // New sale mode - reset form
      setType("retail");
      setLines([]);
      setBillDiscountPct(0);
      setFlatTotal(0);
      setCashAmount(0);
      setUpiAmount(0);
      setCardAmount(0);
      setCreditAmount(0);
      setStoreCreditAmount(0);
      setAvailableStoreCredit(0);
      setOriginalStoreCreditInSale(0); // Reset for new sale
      setStoreExcessAsCredit(false);
      setCustomer(null);
      setCustomerName("");
      setCustQ("");
      setCustResults([]);
      setCustStats(null);
      setNotes("");
      setReason("");
      setQ("");
      setResults([]);
      setScannerOpen(false);
      setStockMap({});
      setSearchStockMap({});
    }
  }, [open, editSale, editReason, shop?.id]);

  // Reset close confirmation when dialog closes
  useEffect(() => {
    if (!open) {
      setShowCloseConfirm(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(custDebRef.current);
    custDebRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", {
          params: custQ.trim() ? { q: custQ, limit: 10 } : { limit: 10 },
        });
        const items = data.items || [];
        setCustResults(items);
        
        // Auto-select customer if phone number matches exactly (10 digits)
        const trimmedQ = custQ.trim();
        if (/^\d{10}$/.test(trimmedQ) && items.length === 1) {
          const c = items[0];
          // Check if the phone matches exactly
          if (c.phone1 === trimmedQ || c.phone2 === trimmedQ) {
            pickCustomer(c);
          }
        }
      } catch (_e) {
        /* silent */
      }
    }, 200);
    return () => clearTimeout(custDebRef.current);
  }, [custQ, open]);

  useEffect(() => {
    if (!open) return;
    if (!q || q.trim().length < 2) {
      setResults([]);
      setSearchStockMap({});
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/products", {
          params: { q, limit: 20, skip: 0 },
        });
        const items = data.items || [];
        setResults(items);
        setHighlight(0);
        
        // If stock lock is enabled, fetch stock availability for search results
        if (stockLockEnabled && shop?.id && items.length > 0) {
          const productIds = items.map(p => p.id).join(",");
          try {
            const stockRes = await api.get("/stock/availability/" + shop.id, {
              params: { product_ids: productIds }
            });
            setSearchStockMap(stockRes.data.items || {});
          } catch {
            setSearchStockMap({});
          }
        } else {
          setSearchStockMap({});
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, stockLockEnabled, shop?.id]);

  // Fetch stock for products in cart when shop or lines change (skip manual items)
  useEffect(() => {
    const realLines = lines.filter((l) => !l.manual && l.product_id);
    if (!open || !shop?.id || realLines.length === 0) {
      setStockMap({});
      setStockBatches({});
      return;
    }
    const productIds = realLines.map((l) => l.product_id).join(",");
    api.get("/stock/batch", { params: { product_ids: productIds } })
      .then((r) => {
        const items = r.data.items || {};
        const map = {};
        const batchMap = {};
        for (const [pid, stocks] of Object.entries(items)) {
          const shopStock = stocks.find((s) => s.shop_id === shop.id);
          map[pid] = shopStock?.quantity ?? null; // null means stock not tracked
          if (shopStock && shopStock.batches && shopStock.batches.length > 0) {
            // Sort batches: earliest expiry first, N/A last (matches backend FIFO)
            const sorted = [...shopStock.batches].sort((a, b) => {
              const aNull = !a.expiry_date;
              const bNull = !b.expiry_date;
              if (aNull && bNull) return 0;
              if (aNull) return 1;
              if (bNull) return -1;
              return a.expiry_date < b.expiry_date ? -1 : a.expiry_date > b.expiry_date ? 1 : 0;
            });
            batchMap[pid] = {
              batches: sorted,
              total: shopStock.quantity || 0,
            };
          }
        }
        setStockMap(map);
        setStockBatches(batchMap);

        // Reconcile batch_id for each line:
        // - If line has no batch_id → auto-pick earliest expiry
        // - If line has a batch_id that no longer exists (stock moved) → flag stale, auto-pick earliest
        const newStale = {};
        setLines((arr) =>
          arr.map((l) => {
            if (l.manual || !l.product_id) return l;
            const available = batchMap[l.product_id]?.batches || [];
            if (available.length === 0) return l;
            if (!l.batch_id) {
              return { ...l, batch_id: available[0].id };
            }
            const found = available.find((b) => b.id === l.batch_id);
            if (!found) {
              // Original batch no longer exists at this shop
              newStale[l.product_id] = {
                reason: "missing",
                original_mrp: l.product_snapshot?.batch_mrp ?? null,
              };
              return { ...l, batch_id: available[0].id };
            }
            if (isEditMode && l.quantity > found.quantity) {
              // Batch still exists but has less stock than the sale line needs
              newStale[l.product_id] = { reason: "short", available: found.quantity };
            }
            return l;
          })
        );
        setStaleBatch(newStale);
      })
      .catch(() => {
        setStockMap({});
        setStockBatches({});
        setStaleBatch({});
      });
  }, [open, shop?.id, lines.length, isEditMode, lines]);

  // Which lines have the batch picker expanded (product_id -> bool)
  const [batchPickerOpen, setBatchPickerOpen] = useState({});
  // Which lines had their saved batch disappear or shrink since the sale was created
  const [staleBatch, setStaleBatch] = useState({});

  // Surface a toast once per edit when stock has moved since the sale was created
  const toastedStaleRef = useRef(false);
  useEffect(() => {
    if (!open) { toastedStaleRef.current = false; return; }
    if (!isEditMode) return;
    const count = Object.keys(staleBatch).length;
    if (count > 0 && !toastedStaleRef.current) {
      toastedStaleRef.current = true;
      toast.warning(
        count === 1
          ? "Stock has moved since this sale — 1 item's batch was re-picked. Review highlighted line."
          : `Stock has moved since this sale — ${count} items' batches were re-picked. Review highlighted lines.`
      );
    }
  }, [open, isEditMode, staleBatch]);

  const addManualLine = (manual) => {
    // manual = { name, type, mrp, discountPct, price, quantity }
    const localId = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setLines((arr) => [
      ...arr,
      {
        local_id: localId,
        product_id: null,
        product_snapshot: {
          product: manual.name,
          brand_name: "",
          auto_barcode: "",
          group_name: manual.type || "",
          mrp: Number(manual.mrp || 0),
        },
        quantity: Number(manual.quantity || 1),
        mrp: Number(manual.mrp || 0),
        unit_price: Number(manual.price || 0),
        line_discount_pct: Number(manual.discountPct || 0),
        price_type: "retail",
        batch_id: null,
        manual: true,
        manual_name: manual.name,
        manual_type: manual.type || "",
      },
    ]);
    toast.success(`Manual item "${manual.name}" added`);
  };

  // Fetch batches for a product and show selection dialog if multiple batches exist
  const fetchBatchesAndAdd = async (product) => {
    // Check if product is already in cart
    if (lines.find((l) => l.product_id === product.id && !l.batch_id)) {
      toast.info("Already added — adjust the quantity or select batches");
      return;
    }
    
    // Stock Lock check first
    if (stockLockEnabled) {
      const available = searchStockMap[product.id];
      if (available === null || available === undefined) {
        toast.error(`Stock Lock: "${product.product}" has no stock at this shop`);
        return;
      }
      if (available <= 0) {
        toast.error(`Stock Lock: "${product.product}" is out of stock`);
        return;
      }
    }
    
    // Fetch batches for this product in the current shop
    try {
      const { data } = await api.get(`/stock/product/${product.id}/batches`, {
        params: { shop_id: shop?.id }
      });
      
      const batches = data.batches || [];
      // Get all batches with quantity > 0 (including expired ones)
      const allAvailableBatches = batches.filter(b => b.quantity > 0);
      const nonExpiredBatches = allAvailableBatches.filter(b => b.expiry_status !== "expired");
      const expiredBatches = allAvailableBatches.filter(b => b.expiry_status === "expired");
      
      if (allAvailableBatches.length === 0) {
        // No batches at all - block if stock lock is enabled
        if (stockLockEnabled) {
          toast.error(`"${product.product}" has no available stock`);
          return;
        }
        // Stock lock is OFF - add product directly without batch
        addLineDirectly(product);
        setQ("");
        setResults([]);
        return;
      }
      
      // Prefer non-expired batches, but allow expired ones with warning
      const batchesToUse = nonExpiredBatches.length > 0 ? nonExpiredBatches : expiredBatches;
      const usingExpiredOnly = nonExpiredBatches.length === 0 && expiredBatches.length > 0;
      
      if (usingExpiredOnly) {
        // Warn user that only expired stock is available
        toast.warning(`"${product.product}" - ITEM EXPIRED. Only expired stock available.`, { duration: 4000 });
      }
      
      if (batchesToUse.length === 1) {
        // Single batch - add directly
        const batch = batchesToUse[0];
        addLineWithBatch(product, batch);
      } else {
        // Multiple batches - show selection dialog (includes expired batches)
        setPendingProduct(product);
        setPendingBatches(allAvailableBatches); // Show all batches including expired
        setBatchSelectOpen(true);
      }
    } catch (e) {
      // Fallback: add without batch selection if API fails
      console.error("Failed to fetch batches:", e);
      addLineDirectly(product);
    }
    
    setQ("");
    setResults([]);
  };

  // Add a line item with a specific batch
  // CRITICAL FIX: Multi-layer protection against race conditions
  // Layer 1: Synchronous ref guard (prevents concurrent additions)
  // Layer 2: Functional state update (checks CURRENT state inside updater)
  // Layer 3: Debounce cooldown (prevents rapid repeated additions)
  const addingBatchesRef = useRef(new Set());
  const lastAddTimeRef = useRef(new Map()); // Track last add time per batch
  const BATCH_ADD_COOLDOWN_MS = 300; // Minimum time between same batch additions
  
  const addLineWithBatch = (product, batch) => {
    const batchKey = `${product.id}:${batch.id}`;
    const now = Date.now();
    
    // Layer 3: Debounce check - prevent rapid duplicate triggers
    const lastAddTime = lastAddTimeRef.current.get(batchKey) || 0;
    if (now - lastAddTime < BATCH_ADD_COOLDOWN_MS) {
      // Silent rejection for rapid scans - user will see the item already added
      return;
    }
    
    // Layer 1: Synchronous guard - check if we're already processing this batch
    if (addingBatchesRef.current.has(batchKey)) {
      toast.info("This batch is already being added...");
      return;
    }
    
    // Pre-check current state (fallback for non-race scenarios)
    if (lines.find((l) => l.product_id === product.id && l.batch_id === batch.id)) {
      toast.info("This batch is already in cart — adjust the quantity");
      return;
    }
    
    // Mark as being added and update timestamp
    addingBatchesRef.current.add(batchKey);
    lastAddTimeRef.current.set(batchKey, now);
    
    const mrp = Number(batch.mrp || product.mrp || 0);
    const unit_price = r2(mrp * (1 - DEFAULT_DISCOUNT / 100));
    const isExpired = batch.expiry_status === "expired";
    
    // Layer 2: Functional update with duplicate check INSIDE the updater
    // This ensures we check against the LATEST state, not stale closure state
    setLines((currentLines) => {
      // Double-check for duplicates using CURRENT state (prevents race condition)
      if (currentLines.find((l) => l.product_id === product.id && l.batch_id === batch.id)) {
        // Already exists - don't add again
        setTimeout(() => {
          toast.info("This batch is already in cart — adjust the quantity");
          addingBatchesRef.current.delete(batchKey);
        }, 0);
        return currentLines; // Return unchanged
      }
      
      // Clear the guard after state update completes
      setTimeout(() => addingBatchesRef.current.delete(batchKey), 100);
      
      return [
        ...currentLines,
        {
          local_id: `${product.id}-${batch.id}-${Date.now()}`,
          product_id: product.id,
          product_snapshot: product,
          quantity: 1,
          mrp,
          unit_price,
          line_discount_pct: DEFAULT_DISCOUNT,
          price_type: "retail",
          batch_id: batch.id,
          batch_info: {
            mrp: batch.mrp,
            expiry_date: batch.expiry_date,
            days_until_expiry: batch.days_until_expiry,
            expiry_status: batch.expiry_status,
          },
          is_expired: isExpired,
        },
      ];
    });
    
    if (isExpired) {
      toast.warning(`Added EXPIRED item: ${product.product} (₹${mrp})`, { duration: 3000 });
    } else {
      toast.success(`Added ${product.product} (₹${mrp})`);
    }
  };

  // Add line directly without batch (fallback for products without batch tracking)
  // Uses same protection pattern as addLineWithBatch
  const addingProductsRef = useRef(new Set());
  const lastAddProductTimeRef = useRef(new Map());
  const PRODUCT_ADD_COOLDOWN_MS = 300;
  
  const addLineDirectly = (product) => {
    const productKey = product.id;
    const now = Date.now();
    
    // Layer 3: Debounce check
    const lastAddTime = lastAddProductTimeRef.current.get(productKey) || 0;
    if (now - lastAddTime < PRODUCT_ADD_COOLDOWN_MS) {
      return;
    }
    
    // Layer 1: Synchronous guard
    if (addingProductsRef.current.has(productKey)) {
      return;
    }
    
    // Pre-check current state
    if (lines.find((l) => l.product_id === product.id && !l.batch_id)) {
      toast.info("Already added — adjust the quantity");
      return;
    }
    
    // Mark as being added
    addingProductsRef.current.add(productKey);
    lastAddProductTimeRef.current.set(productKey, now);
    
    const mrp = Number(product.highest_mrp || product.mrp || 0);
    const unit_price = r2(mrp * (1 - DEFAULT_DISCOUNT / 100));
    
    // Layer 2: Functional update with duplicate check inside
    setLines((currentLines) => {
      if (currentLines.find((l) => l.product_id === product.id && !l.batch_id)) {
        setTimeout(() => {
          toast.info("Already added — adjust the quantity");
          addingProductsRef.current.delete(productKey);
        }, 0);
        return currentLines;
      }
      
      setTimeout(() => addingProductsRef.current.delete(productKey), 100);
      
      return [
        ...currentLines,
        {
          local_id: `${product.id}-nobatch-${Date.now()}`,
          product_id: product.id,
          product_snapshot: product,
          quantity: 1,
          mrp,
          unit_price,
          line_discount_pct: DEFAULT_DISCOUNT,
          price_type: "retail",
          batch_id: null,
        },
      ];
    });
  };

  // Handle batch selection dialog confirm - add selected batches as separate line items
  // This function handles bulk additions from the batch selection dialog
  const handleBatchSelectionConfirm = (lineItems) => {
    if (!lineItems || lineItems.length === 0) return;
    
    // Use functional state update to handle all additions atomically
    setLines((currentLines) => {
      const updatedLines = [...currentLines];
      const newLines = [];
      
      for (const item of lineItems) {
        // Check against CURRENT state inside the functional update
        const existingIdx = updatedLines.findIndex(
          (l) => l.product_id === item.product_id && l.batch_id === item.batch_id
        );
        
        if (existingIdx >= 0) {
          // Update existing line quantity
          updatedLines[existingIdx] = {
            ...updatedLines[existingIdx],
            quantity: updatedLines[existingIdx].quantity + item.quantity,
          };
        } else {
          // Check if we're about to add a duplicate in this same batch of additions
          const alreadyInNewLines = newLines.find(
            (l) => l.product_id === item.product_id && l.batch_id === item.batch_id
          );
          if (alreadyInNewLines) {
            // Merge quantity into the pending new line
            alreadyInNewLines.quantity += item.quantity;
          } else {
            newLines.push({
              ...item,
              local_id: `${item.product_id}-${item.batch_id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            });
          }
        }
      }
      
      return [...updatedLines, ...newLines];
    });
    
    const totalQty = lineItems.reduce((sum, l) => sum + l.quantity, 0);
    const batchCount = new Set(lineItems.map(l => l.batch_id)).size;
    toast.success(
      batchCount === 1
        ? `Added ${pendingProduct?.product} (${totalQty} units)`
        : `Added ${pendingProduct?.product} from ${batchCount} batches (${totalQty} units)`
    );
    
    // Reset pending state
    setPendingProduct(null);
    setPendingBatches([]);
  };

  // Legacy addLine - now calls fetchBatchesAndAdd
  const addLine = (product) => {
    fetchBatchesAndAdd(product);
  };

  // Keyboard navigation handler for line item inputs
  const handleLineInputKeyDown = (e, idx, field, productId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const refs = lineRefs.current[productId];
      if (!refs) return;
      
      // Navigate: DP → Price → Qty → Search box
      if (field === "dp" && refs.price) {
        refs.price.focus();
        refs.price.select();
      } else if (field === "price" && refs.qty) {
        refs.qty.focus();
        refs.qty.select();
      } else if (field === "qty") {
        // Always return to search box after QTY
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    } else if (e.key === "+" || e.key === "=") {
      if (field === "dp" || field === "qty") {
        e.preventDefault();
        const currentValue = field === "dp" 
          ? Number(lines[idx].line_discount_pct || 0)
          : Number(lines[idx].quantity || 0);
        updateLine(idx, { [field === "dp" ? "line_discount_pct" : "quantity"]: currentValue + 1 });
      }
    } else if (e.key === "-" || e.key === "_") {
      if (field === "dp" || field === "qty") {
        e.preventDefault();
        const currentValue = field === "dp"
          ? Number(lines[idx].line_discount_pct || 0)
          : Number(lines[idx].quantity || 0);
        const newValue = Math.max(field === "qty" ? 1 : 0, currentValue - 1);
        updateLine(idx, { [field === "dp" ? "line_discount_pct" : "quantity"]: newValue });
      }
    }
  };

  const updateLine = (idx, patch) => {
    setLines((arr) =>
      arr.map((l, i) => {
        if (i !== idx) return l;
        const next = { ...l, ...patch };
        
        // Handle price_type toggle with wholesale price auto-fill
        if ("price_type" in patch && patch.price_type !== l.price_type) {
          const product = l.product_snapshot;
          const wholesalePrice = product?.wholesale_price;
          
          if (patch.price_type === "wholesale" && wholesalePrice && wholesalePrice > 0) {
            // Switching to Wholesale with a wholesale price - auto-fill
            next.unit_price = r2(wholesalePrice);
            const mrp = Number(next.mrp || 0);
            next.line_discount_pct = mrp > 0 ? r2(((mrp - wholesalePrice) / mrp) * 100) : 0;
          }
          // If switching to Retail or no wholesale price, keep current values (editable)
        }
        
        // If MRP changed, recompute unit_price maintaining discount %
        if ("mrp" in patch) {
          const newMrp = Number(patch.mrp || 0);
          const pct = Number(next.line_discount_pct || 0);
          next.mrp = newMrp;
          next.unit_price = r2(newMrp * (1 - pct / 100));
        }
        
        // If discount % changed, recompute unit_price from MRP
        if ("line_discount_pct" in patch) {
          const pct = Math.max(0, Math.min(100, Number(patch.line_discount_pct || 0)));
          next.line_discount_pct = pct;
          next.unit_price = r2(Number(next.mrp || 0) * (1 - pct / 100));
        }
        
        // If unit_price changed, recompute discount %
        if ("unit_price" in patch) {
          const up = Number(patch.unit_price || 0);
          next.unit_price = up;
          const mrp = Number(next.mrp || 0);
          next.line_discount_pct = mrp > 0 ? r2(((mrp - up) / mrp) * 100) : 0;
        }
        return next;
      }),
    );
  };

  const removeLine = (idx) => setLines((arr) => arr.filter((_, i) => i !== idx));

  const totalMRP = useMemo(
    () => r2(lines.reduce((a, l) => a + Number(l.mrp || 0) * Number(l.quantity || 0), 0)),
    [lines],
  );
  
  const subtotal = useMemo(
    () => r2(lines.reduce((a, l) => a + Number(l.unit_price || 0) * Number(l.quantity || 0), 0)),
    [lines],
  );
  
  const billDiscAmt = useMemo(
    () => r2((subtotal * Number(billDiscountPct || 0)) / 100),
    [subtotal, billDiscountPct],
  );
  
  const totalBeforeRoundoff = r2(subtotal - billDiscAmt);
  
  // Auto-calculate round-off to nearest whole number (used when no flat total specified)
  const autoRoundOff = useMemo(() => {
    const rounded = Math.round(totalBeforeRoundoff);
    return r2(rounded - totalBeforeRoundoff);
  }, [totalBeforeRoundoff]);
  
  // If user entered a flat total, calculate round-off to reach it
  // Otherwise, use the auto round-off to nearest whole number
  const effectiveRoundOff = useMemo(() => {
    if (flatTotal > 0) {
      // User wants a specific final total, calculate the required round-off
      return r2(flatTotal - totalBeforeRoundoff);
    }
    return autoRoundOff;
  }, [flatTotal, totalBeforeRoundoff, autoRoundOff]);
  
  const total = r2(totalBeforeRoundoff + effectiveRoundOff);
  
  const productCount = lines.length;
  const totalQuantity = lines.reduce((a, l) => a + Number(l.quantity || 0), 0);

  // Auto-fill payments: Store Credit first (up to bill total), then Cash for remainder
  // SKIP in edit mode - payments are already loaded from the existing sale
  useEffect(() => {
    if (!open || total === 0 || lines.length === 0) return;
    
    // Skip auto-fill in edit mode - use the existing payment breakdown
    if (editSale) return;
    
    // Calculate how much store credit to use (min of available and total)
    const storeCreditToUse = availableStoreCredit > 0 
      ? Math.min(availableStoreCredit, total) 
      : 0;
    
    // Calculate remaining after store credit and other payments (UPI, Card, Credit)
    const otherPayments = r2(upiAmount + cardAmount + creditAmount);
    const remainingAfterStoreCredit = r2(total - storeCreditToUse - otherPayments);
    
    // Set store credit amount
    setStoreCreditAmount(r2(storeCreditToUse));
    
    // Set cash to cover the remainder (non-negative)
    setCashAmount(Math.max(0, remainingAfterStoreCredit));
  }, [total, availableStoreCredit, upiAmount, cardAmount, creditAmount, open, lines.length, editSale]);

  const pickCustomer = async (c) => {
    setCustomer(c);
    setCustomerName(c.name || "");
    setCustQ(c.name || "");
    setCustResults([]);
    setCustFocus(false);
    // Customer type (B2C/SHP/FLC) is identity-only and does NOT drive sale type.
    // Sale type default comes from the customer's *last* sale (retail/wholesale pricing), if any.
    try {
      const { data } = await api.get(`/customers/${c.id}/stats`);
      setCustStats(data);
      if (data.last_type === "retail" || data.last_type === "wholesale") {
        setType(data.last_type);
      }
      // Set outstanding dues (receivable = customer owes shop)
      setOutstandingDues(data.credit_owed || 0);
    } catch (_e) {
      setCustStats(null);
      setOutstandingDues(0);
    }
    // Fetch store credit balance
    try {
      const { data } = await api.get(`/customers/${c.id}/store-credit`);
      setAvailableStoreCredit(data.available || 0);
    } catch (_e) {
      setAvailableStoreCredit(0);
    }
  };

  const clearCustomer = () => {
    setCustomer(null);
    setCustomerName("");
    setCustQ("");
    setCustStats(null);
    setAvailableStoreCredit(0);
    setStoreCreditAmount(0);
    setOutstandingDues(0);
    setDuesCollectionAmount(0);
  };

  const submit = async () => {
    if (!shop) return toast.error("Pick a shop first");
    if (!lines.length) return toast.error("Add at least one item");
    
    // Validate stock availability for products that have stock tracking enabled
    // (Skip manual items - they bypass inventory entirely)
    for (const line of lines) {
      if (line.manual || !line.product_id) continue;
      const info = stockBatches[line.product_id];
      const selBatch = info?.batches?.find((b) => b.id === line.batch_id);
      const available = selBatch ? selBatch.quantity : stockMap[line.product_id];
      if (available != null && line.quantity > available) {
        const productName = line.product_snapshot?.product || "Product";
        return toast.error(
          `${productName}: Only ${available} in ${selBatch ? "selected batch" : "stock"}, but ${line.quantity} requested`
        );
      }
    }
    
    // Validate reason in edit mode
    if (isEditMode && !reason.trim()) {
      return toast.error("Please add a reason for this edit");
    }
    
    // Validate store credit doesn't exceed effective available
    // In edit mode, effective available = current available + original SC from this sale
    const effectiveAvailableCredit = availableStoreCredit + originalStoreCreditInSale;
    if (storeCreditAmount > effectiveAvailableCredit + 0.01) {
      return toast.error(`Store credit exceeds available balance (₹${effectiveAvailableCredit.toFixed(2)})`);
    }
    
    // Validate payments total
    const paymentsTotal = r2(cashAmount + upiAmount + cardAmount + creditAmount + storeCreditAmount);
    const totalRounded = r2(total);
    const excessAmount = r2(paymentsTotal - totalRounded);
    
    // Check for shortfall (always an error)
    if (excessAmount < -0.01) {
      return toast.error(`Payments total (₹${paymentsTotal.toFixed(2)}) is ₹${Math.abs(excessAmount).toFixed(2)} short of sale total (₹${totalRounded.toFixed(2)})`);
    }
    
    // Check for excess - must have customer and checkbox checked to allow
    if (excessAmount > 0.01) {
      if (!customer) {
        return toast.error("Excess payment requires a customer to store as credit");
      }
      if (!storeExcessAsCredit) {
        return toast.error(`Payments exceed total by ₹${excessAmount.toFixed(2)}. Check "Store as credit" to proceed.`);
      }
    }
    
    // Build payments array (only non-zero amounts)
    const payments = [];
    if (cashAmount > 0) payments.push({ mode: "cash", amount: r2(cashAmount) });
    if (upiAmount > 0) payments.push({ mode: "upi", amount: r2(upiAmount) });
    if (cardAmount > 0) payments.push({ mode: "card", amount: r2(cardAmount) });
    if (creditAmount > 0) payments.push({ mode: "credit", amount: r2(creditAmount) });
    if (storeCreditAmount > 0) payments.push({ mode: "store_credit", amount: r2(storeCreditAmount) });
    
    if (payments.length === 0) {
      return toast.error("Add at least one payment");
    }
    
    setSaving(true);
    try {
      const payload = {
        shop_id: shop.id,
        type,
        line_items: lines.map((l) => ({
          product_id: l.manual ? null : l.product_id,
          quantity: Number(l.quantity || 1),
          mrp: Number(l.mrp || 0),
          unit_price: Number(l.unit_price || 0),
          line_discount_pct: Number(l.line_discount_pct || 0),
          price_type: l.price_type || "retail",
          batch_id: l.manual ? null : (l.batch_id || null),
          manual: !!l.manual,
          manual_name: l.manual ? (l.manual_name || l.product_snapshot?.product || "") : null,
          manual_type: l.manual ? (l.manual_type || l.product_snapshot?.group_name || "") : null,
        })),
        bill_discount_pct: Number(billDiscountPct || 0),
        round_off_amount: effectiveRoundOff, // Use the effective round-off, not manual entry
        payments,
        customer_id: customer?.id || null,
        customer_name: customer ? customer.name : customerName,
        notes,
        // Excess payment handling
        excess_as_credit: storeExcessAsCredit && excessAmount > 0.01 ? r2(excessAmount) : 0,
        // Outstanding dues collection (not part of sale total, affects drawer)
        dues_collection: duesCollectionAmount > 0 ? {
          amount: r2(duesCollectionAmount),
          mode: duesCollectionMode,
        } : null,
      };
      
      if (isEditMode) {
        // Edit mode: Check if payment adjustment is needed
        const paymentDifference = r2(total - originalPaidAmount);
        
        // If there's a payment difference and sale was previously paid, show adjustment dialog
        if (Math.abs(paymentDifference) > 0.01 && originalPaidAmount > 0) {
          // Store the payload and show adjustment dialog
          setPendingEditPayload({
            ...payload,
            reason: reason.trim(),
          });
          setPaymentAdjustmentOpen(true);
          setSaving(false);
          return;
        }
        
        // No payment adjustment needed, just update the sale
        await api.patch(`/sales/${editSale.id}`, {
          ...payload,
          reason: reason.trim(),
        });
        toast.success("Sale updated successfully");
      } else {
        // New sale mode: POST
        const { data } = await api.post("/sales", payload);
        toast.success(`Sale recorded · ₹${Number(data.total).toFixed(2)}`);
      }
      
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Handle payment adjustment confirmation (for edit mode with payment changes)
  const handlePaymentAdjustment = async (adjustmentData) => {
    if (!pendingEditPayload || !editSale) return;
    
    setSaving(true);
    try {
      // First update the sale with the new items
      // STEP 2F: Add skip_payment_ledger flag AND pending_adjustment_id
      // This links the PATCH to the subsequent payment-adjustment call
      // Backend validates that the same adjustment_id is used with matching parameters
      await api.patch(`/sales/${editSale.id}`, {
        ...pendingEditPayload,
        skip_payment_ledger: true,
        pending_adjustment_id: adjustmentData.adjustment_id,  // STEP 2F: Links PATCH to payment-adjustment
      });
      
      // Then process the payment adjustment with idempotency key
      // The adjustment_id ensures exactly-once semantics on retry
      const adjustmentPayload = {
        adjustment_id: adjustmentData.adjustment_id,  // Idempotency key from dialog
        mode: adjustmentData.mode,
        amount: adjustmentData.amount,
        is_refund: adjustmentData.isRefund,
        reason: pendingEditPayload.reason || "EDIT SALE PAYMENT ADJUSTMENT",
      };
      
      await api.post(`/sales/${editSale.id}/payment-adjustment`, adjustmentPayload);
      
      const actionText = adjustmentData.isRefund 
        ? `Refunded ₹${adjustmentData.amount.toFixed(2)} via ${adjustmentData.mode.replace('_', ' ')}`
        : `Collected ₹${adjustmentData.amount.toFixed(2)} via ${adjustmentData.mode}`;
      
      toast.success(`Sale updated. ${actionText}`);
      
      setPaymentAdjustmentOpen(false);
      setPendingEditPayload(null);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ===== TWO-STEP POS CHECKOUT: New Sale Payment Flow =====
  
  // Step 1 → Step 2: Open payment summary dialog (validates cart first)
  const proceedToPayment = () => {
    if (!shop) return toast.error("Pick a shop first");
    if (!lines.length) return toast.error("Add at least one item");
    
    // Validate stock availability before proceeding
    for (const line of lines) {
      if (line.manual || !line.product_id) continue;
      const info = stockBatches[line.product_id];
      const selBatch = info?.batches?.find((b) => b.id === line.batch_id);
      const available = selBatch ? selBatch.quantity : stockMap[line.product_id];
      if (available != null && line.quantity > available) {
        const productName = line.product_snapshot?.product || "Product";
        return toast.error(
          `${productName}: Only ${available} in ${selBatch ? "selected batch" : "stock"}, but ${line.quantity} requested`
        );
      }
    }
    
    // Open payment summary dialog
    setPaymentSummaryOpen(true);
  };
  
  // Step 2 → Back to Step 1: Return to cart (preserves all data)
  const backToSale = () => {
    setPaymentSummaryOpen(false);
    // Cart data is preserved - no state reset needed
  };
  
  // Step 2 → Complete: Confirm payment and create sale
  const confirmPaymentAndComplete = async (paymentData) => {
    if (!shop || !lines.length) return;
    
    setSaving(true);
    try {
      const payload = {
        shop_id: shop.id,
        type,
        line_items: lines.map((l) => ({
          product_id: l.manual ? null : l.product_id,
          quantity: Number(l.quantity || 1),
          mrp: Number(l.mrp || 0),
          unit_price: Number(l.unit_price || 0),
          line_discount_pct: Number(l.line_discount_pct || 0),
          price_type: l.price_type || "retail",
          batch_id: l.manual ? null : (l.batch_id || null),
          manual: !!l.manual,
          manual_name: l.manual ? (l.manual_name || l.product_snapshot?.product || "") : null,
          manual_type: l.manual ? (l.manual_type || l.product_snapshot?.group_name || "") : null,
        })),
        bill_discount_pct: Number(billDiscountPct || 0),
        round_off_amount: effectiveRoundOff,
        payments: paymentData.payments,
        customer_id: customer?.id || null,
        customer_name: customer ? customer.name : customerName,
        notes,
        excess_as_credit: paymentData.excessAsCredit || 0,
        dues_collection: paymentData.duesCollection || null,
      };
      
      // Create sale via existing backend endpoint
      const { data } = await api.post("/sales", payload);
      toast.success(`Sale recorded · ₹${Number(data.total).toFixed(2)}`);
      
      // Close both dialogs and trigger refresh
      setPaymentSummaryOpen(false);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Barcode scanner debounce - prevents rapid repeated Enter keys
  const lastEnterTimeRef = useRef(0);
  const ENTER_KEY_DEBOUNCE_MS = 300;
  
  const onSearchKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      
      // Debounce Enter key to prevent rapid scanner inputs
      const now = Date.now();
      if (now - lastEnterTimeRef.current < ENTER_KEY_DEBOUNCE_MS) {
        // Silent rejection - scanner fired multiple times
        return;
      }
      lastEnterTimeRef.current = now;
      
      if (highlight >= 0 && highlight < results.length) addLine(results[highlight]);
    }
  };

  return (
    <>
      {/* Main Sale Dialog */}
      <Dialog open={open} onOpenChange={() => {/* Controlled: prevent default close behavior */}}>
        <DialogContent
          className="sm:max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="new-sale-dialog"
          hideCloseButton={true}
          onPointerDownOutside={(e) => e.preventDefault()} // Prevent close on outside click
          onEscapeKeyDown={(e) => e.preventDefault()} // Prevent close on Escape key
          onInteractOutside={(e) => e.preventDefault()} // Extra safety: prevent any outside interaction close
        >
          {/* Custom header with minimize and close buttons */}
          <div className="flex items-start justify-between -mt-2 -mx-2 mb-2">
            <DialogHeader className="flex-1 min-w-0">
              <DialogTitle className="font-heading tracking-tight">
                {isEditMode ? `Edit sale · ${shop?.name}` : `New sale · ${shop?.name}`}
              </DialogTitle>
              <DialogDescription>
                {isEditMode 
                  ? "Modify products, quantities, or payments. A reason is required for auditing."
                  : "Toggle R (Retail) or W (Wholesale) for each product. Default 5% discount from MRP."}
              </DialogDescription>
            </DialogHeader>
            {/* Custom close/minimize buttons - only for new sales */}
            {!isEditMode && (
              <div className="flex items-center gap-1 shrink-0 ml-2">
                <button
                  type="button"
                  onClick={handleRequestClose}
                  className="rounded-sm p-1.5 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  data-testid="sale-close-btn"
                  title="Close sale"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
            )}
            {/* For edit mode, show simple close (no confirmation needed for empty edits) */}
            {isEditMode && (
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-sm p-1.5 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                data-testid="sale-close-btn"
                title="Close"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            )}
          </div>

        {/* Stock Lock indicator banner */}
        {stockLockEnabled && (
          <div 
            className="flex items-center gap-2 mt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200"
            data-testid="stock-lock-banner"
          >
            <Lock className="w-4 h-4 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-amber-800">Stock Lock Active</span>
              <span className="text-xs text-amber-600 ml-2">Only products with available stock can be added · Manual items are exempt</span>
            </div>
          </div>
        )}

        {/* Customer picker + stats panel (top) */}
        <div className="mt-3 grid md:grid-cols-3 gap-3" data-testid="sale-customer-section">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Customer
            </Label>
            <div className="relative mt-1">
              {customer ? (
                <div className="flex items-center gap-2 p-2 pl-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
                  <UserIcon className="w-4 h-4 text-[#9E6D5B]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{customer.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {customer.place ? customer.place + " · " : ""}
                      {customer.phone1 || "—"}
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={clearCustomer}
                    data-testid="sale-clear-customer"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={custQ}
                      onChange={(e) => setCustQ(e.target.value)}
                      onFocus={() => setCustFocus(true)}
                      onBlur={() => setTimeout(() => setCustFocus(false), 150)}
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const query = custQ.trim();
                          // Check if it's a phone number (10 digits)
                          if (/^\d{10}$/.test(query)) {
                            try {
                              const { data } = await api.get("/customers", { params: { q: query, limit: 1 } });
                              if (data.items && data.items.length > 0) {
                                // Customer found - auto-select
                                pickCustomer(data.items[0]);
                              } else {
                                // Customer not found - open add form with phone pre-filled
                                setAddCustOpen(true);
                              }
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }
                        }
                      }}
                      placeholder="Search customer by name, phone or place…"
                      className="pl-9"
                      data-testid="sale-customer-search"
                    />
                    {custFocus && custResults.length > 0 && (
                      <div
                        className="absolute z-20 bg-white w-full border border-[hsl(var(--border))] rounded-md mt-1 max-h-64 overflow-y-auto shadow-lg"
                        data-testid="sale-customer-results"
                      >
                        {custResults.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              pickCustomer(c);
                            }}
                            className="w-full text-left p-2 hover:bg-[hsl(var(--muted))] flex items-center gap-2"
                            data-testid={`sale-customer-opt-${c.id}`}
                          >
                            <UserIcon className="w-3.5 h-3.5 text-[#9E6D5B]" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{c.name}</div>
                              <div className="text-[10px] text-muted-foreground truncate">
                                {c.place ? c.place + " · " : ""}
                                {c.phone1 || "—"}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAddCustOpen(true)}
                    data-testid="sale-add-customer-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" /> Add
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Stats panel */}
          <div
            className="rounded-md border border-[hsl(var(--border))] p-3 bg-[#FBF6F2]"
            data-testid="sale-customer-stats"
          >
            {!customer ? (
              <div className="text-xs text-muted-foreground">
                Pick a customer to see their purchase history.
              </div>
            ) : !custStats ? (
              <div className="text-xs text-muted-foreground">Loading stats…</div>
            ) : custStats.count === 0 ? (
              <div className="text-xs text-muted-foreground">
                First purchase — no history yet.
              </div>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Lifetime spend
                </div>
                <div className="font-mono-data text-xl font-semibold text-[#9E6D5B]">
                  ₹{Number(custStats.total_spent || 0).toFixed(2)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  across {custStats.count} sale{custStats.count === 1 ? "" : "s"}
                </div>
                {/* Store Credit Available */}
                {custStats.store_credit > 0 && (
                  <div className="mt-2 pt-2 border-t border-[hsl(var(--border))]">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">
                      Store Credit Available
                    </div>
                    <div className="text-lg font-mono-data font-bold text-emerald-600">
                      ₹{Number(custStats.store_credit).toFixed(2)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Can be used in this sale
                    </div>
                  </div>
                )}
                {/* Credit Owed */}
                {custStats.credit_owed > 0 && (
                  <div className="mt-2 pt-2 border-t border-[hsl(var(--border))]">
                    <div className="text-[10px] uppercase tracking-widest text-orange-600 font-semibold">
                      Outstanding Dues
                    </div>
                    <div className="text-lg font-mono-data font-bold text-orange-600">
                      ₹{Number(custStats.credit_owed).toFixed(2)}
                    </div>
                  </div>
                )}
                {custStats.last_purchase_at && (
                  <div className="mt-2 pt-2 border-t border-[hsl(var(--border))]">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Last purchase
                    </div>
                    <div className="text-xs font-mono-data">
                      ₹{Number(custStats.last_purchase_amount || 0).toFixed(2)} ·{" "}
                      {custStats.last_type}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(custStats.last_purchase_at).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                      {custStats.last_shop_name ? ` · ${custStats.last_shop_name}` : ""}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Lines - ABOVE search box */}
        {lines.length > 0 && (
          <div className="mt-4 border border-[hsl(var(--border))] rounded-md overflow-hidden">
            {/* Column header (desktop only) */}
            <div
              className="hidden md:grid grid-cols-12 gap-3 items-center px-3 py-1.5 bg-[#f8f9fb] border-b border-[hsl(var(--border))]"
              data-testid="sale-cart-header"
            >
              <div className="col-span-3 text-[10px] uppercase tracking-wider text-muted-foreground">Item</div>
              <div className="col-span-1 text-[10px] uppercase tracking-wider text-muted-foreground text-center">Type</div>
              <div className="col-span-1 text-[10px] uppercase tracking-wider text-muted-foreground text-center">MRP</div>
              <div className="col-span-1 text-[10px] uppercase tracking-wider text-muted-foreground text-center">Disc %</div>
              <div className="col-span-2 text-[10px] uppercase tracking-wider text-muted-foreground text-center">Price</div>
              <div className="col-span-2 text-[10px] uppercase tracking-wider text-muted-foreground text-center">Qty</div>
              <div className="col-span-2 text-[10px] uppercase tracking-wider text-muted-foreground text-right">Total</div>
            </div>
            <div className="divide-y divide-[hsl(var(--border))]">
              {lines.map((l, idx) => {
              const p = l.product_snapshot || {};
              const isManual = !!l.manual;
              const lineKey = l.local_id || l.product_id;
              const amount = r2(Number(l.unit_price || 0) * Number(l.quantity || 0));
              // Available qty = selected batch's qty (if any); fallback to shop-total stockMap
              // Manual items have no stock tracking.
              const info = isManual ? null : stockBatches[l.product_id];
              const selectedBatch = info?.batches?.find((b) => b.id === l.batch_id);
              const availableQty = isManual
                ? null
                : selectedBatch
                  ? selectedBatch.quantity
                  : stockMap[l.product_id];
              return (
                <div 
                  key={lineKey} 
                  className={`${l.price_type === "wholesale" ? "bg-purple-50/70 border-l-4 border-purple-400" : "bg-white"}`}
                  data-testid={`sale-line-${lineKey}`}
                >
                  {/* Row 1: Full Product Name */}
                  <div className="px-3 pt-2 pb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="font-medium text-sm truncate" title={p.product}>{p.product}</div>
                      {isManual && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase shrink-0 border border-amber-200"
                          title="Temporary manual bill item — not tracked in inventory"
                          data-testid={`sale-line-manual-badge-${lineKey}`}
                        >
                          Manual
                        </span>
                      )}
                      {l.price_type === "wholesale" && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-semibold uppercase shrink-0 border border-purple-200"
                          title="Wholesale pricing"
                          data-testid={`sale-line-wholesale-badge-${lineKey}`}
                        >
                          Wholesale
                        </span>
                      )}
                    </div>
                    {/* Hide delete button for original items in edit mode - use Sales Return instead */}
                    {!(isEditMode && l.isOriginal) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => removeLine(idx)}
                        data-testid={`sale-line-remove-${lineKey}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Row 2: Clean Grid Layout (desktop) — Brand+Code | Type | MRP | DP | Price | Qty | Total */}
                  <div className="hidden md:grid grid-cols-12 gap-3 items-center px-3 pb-2">
                    {/* Brand & Barcode (or Type for manual items) + Batch Info + Expired Badge */}
                    <div
                      className="col-span-3 text-[12px] text-muted-foreground font-mono-data truncate"
                      title={
                        isManual
                          ? `Manual item${p.group_name ? ` · ${p.group_name}` : ""}`
                          : `${p.brand_name || ""} · Auto: ${p.auto_barcode || "—"}${p.manual_barcode ? ` · Manual: ${p.manual_barcode}` : ""}`
                      }
                    >
                      {isManual
                        ? (p.group_name ? `Type: ${p.group_name}` : "Manual item")
                        : (
                          <span className="flex items-center gap-1 flex-wrap">
                            <span className="truncate">{p.brand_name} · {p.auto_barcode || "—"}{p.manual_barcode ? ` / ${p.manual_barcode}` : ""}</span>
                            {/* EXPIRED badge - shown prominently for expired items */}
                            {l.is_expired && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300 font-bold shrink-0 animate-pulse">
                                EXPIRED
                              </span>
                            )}
                            {l.batch_info && !l.is_expired && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 shrink-0">
                                Exp: {l.batch_info.expiry_date 
                                  ? new Date(l.batch_info.expiry_date).toLocaleString("en-US", { month: "short", year: "2-digit" })
                                  : "N/A"}
                              </span>
                            )}
                          </span>
                        )}
                    </div>

                    {/* Type toggle */}
                    <div className="col-span-1 flex justify-center">
                      <button
                        type="button"
                        onClick={() => updateLine(idx, { price_type: l.price_type === "retail" ? "wholesale" : "retail" })}
                        className={`h-8 w-10 rounded-md text-xs font-semibold transition-colors ${
                          l.price_type === "retail"
                            ? "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                            : "bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-300"
                        }`}
                        title={`Toggle to ${l.price_type === "retail" ? "wholesale" : "retail"} pricing${l.product_snapshot?.wholesale_price ? ` (W: ₹${l.product_snapshot.wholesale_price})` : ""}`}
                        data-testid={`sale-line-type-${lineKey}`}
                      >
                        {l.price_type === "retail" ? "R" : "W"}
                      </button>
                    </div>

                    {/* MRP */}
                    <div className="col-span-1">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={l.mrp}
                        onChange={(e) => updateLine(idx, { mrp: parseFloat(e.target.value || "0") })}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
                        }}
                        className="h-8 bg-[#f4f5f7] border-0 focus-visible:ring-1 focus-visible:ring-[#9E6D5B]/40 text-center font-mono-data text-xs rounded-md px-1"
                        data-testid={`sale-line-mrp-${lineKey}`}
                      />
                    </div>

                    {/* DP % */}
                    <div className="col-span-1">
                      <Input
                        ref={(el) => {
                          if (!lineRefs.current[lineKey]) lineRefs.current[lineKey] = {};
                          lineRefs.current[lineKey].dp = el;
                        }}
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        value={l.line_discount_pct}
                        onChange={(e) =>
                          updateLine(idx, { line_discount_pct: parseFloat(e.target.value || "0") })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          } else {
                            handleLineInputKeyDown(e, idx, "dp", lineKey);
                          }
                        }}
                        className="h-8 bg-[#f4f5f7] border-0 focus-visible:ring-1 focus-visible:ring-[#9E6D5B]/40 text-center font-mono-data text-xs rounded-md px-1"
                        data-testid={`sale-line-disc-${lineKey}`}
                      />
                    </div>

                    {/* Price */}
                    <div className="col-span-2">
                      <Input
                        ref={(el) => {
                          if (!lineRefs.current[lineKey]) lineRefs.current[lineKey] = {};
                          lineRefs.current[lineKey].price = el;
                        }}
                        type="number"
                        step="0.01"
                        min={0}
                        value={l.unit_price}
                        onChange={(e) =>
                          updateLine(idx, { unit_price: parseFloat(e.target.value || "0") })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          } else {
                            handleLineInputKeyDown(e, idx, "price", lineKey);
                          }
                        }}
                        className="h-8 bg-[#f4f5f7] border-0 focus-visible:ring-1 focus-visible:ring-[#9E6D5B]/40 text-center font-mono-data text-xs rounded-md px-2"
                        data-testid={`sale-line-price-${lineKey}`}
                      />
                    </div>

                    {/* Qty + availability */}
                    <div className="col-span-2 flex items-center gap-1 justify-center">
                      <Input
                        ref={(el) => {
                          if (!lineRefs.current[lineKey]) lineRefs.current[lineKey] = {};
                          lineRefs.current[lineKey].qty = el;
                          qtyInputRefs.current[lineKey] = el;
                        }}
                        type="number"
                        min={1}
                        max={availableQty != null ? availableQty : undefined}
                        value={l.quantity}
                        onChange={(e) => {
                          const newQty = Math.max(1, parseInt(e.target.value || "1"));
                          if (availableQty != null && newQty > availableQty) {
                            toast.error(`Only ${availableQty} available in ${selectedBatch ? "selected batch" : "stock"}`);
                            updateLine(idx, { quantity: availableQty > 0 ? availableQty : 1 });
                          } else {
                            updateLine(idx, { quantity: newQty });
                          }
                        }}
                        onKeyDown={(e) => handleLineInputKeyDown(e, idx, "qty", lineKey)}
                        className={`h-8 w-14 bg-[#f4f5f7] border-0 focus-visible:ring-1 focus-visible:ring-[#9E6D5B]/40 text-center font-mono-data text-xs rounded-md px-1 ${
                          availableQty != null && l.quantity > availableQty
                            ? "ring-1 ring-red-500 bg-red-50"
                            : ""
                        }`}
                        data-testid={`sale-line-qty-${lineKey}`}
                      />
                      {availableQty != null && (
                        <span
                          className={`text-[11px] font-mono-data ${
                            l.quantity > availableQty ? "text-red-600 font-semibold" : "text-muted-foreground"
                          }`}
                          title={
                            selectedBatch
                              ? `${availableQty} available in selected batch`
                              : `${availableQty} available in stock at this shop`
                          }
                        >
                          /{availableQty}
                        </span>
                      )}
                    </div>

                    {/* Total */}
                    <div
                      className="col-span-2 text-right font-mono-data font-bold text-sm text-slate-900"
                      data-testid={`sale-line-amount-${lineKey}`}
                    >
                      ₹{amount.toFixed(2)}
                    </div>
                  </div>

                  {/* Row 2 (mobile): brand+total on top, inputs below */}
                  <div className="md:hidden px-3 pb-2 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[12px] text-muted-foreground font-mono-data truncate flex-1 min-w-0">
                        <span className="truncate block">
                          {isManual
                            ? (p.group_name ? `Type: ${p.group_name}` : "Manual item")
                            : `${p.brand_name} · ${p.auto_barcode || "—"}${p.manual_barcode ? ` / ${p.manual_barcode}` : ""}`}
                        </span>
                        {/* EXPIRED badge for mobile - shown prominently */}
                        {l.is_expired && (
                          <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300 font-bold mt-1 animate-pulse">
                            EXPIRED
                          </span>
                        )}
                        {l.batch_info && !l.is_expired && (
                          <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 mt-1">
                            Exp: {l.batch_info.expiry_date 
                              ? new Date(l.batch_info.expiry_date).toLocaleString("en-US", { month: "short", year: "2-digit" })
                              : "N/A"}
                          </span>
                        )}
                      </div>
                      <div className="font-mono-data font-bold text-sm text-slate-900 shrink-0">
                        ₹{amount.toFixed(2)}
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-1.5">
                      <button
                        type="button"
                        onClick={() => updateLine(idx, { price_type: l.price_type === "retail" ? "wholesale" : "retail" })}
                        className={`h-8 rounded-md text-xs font-semibold ${
                          l.price_type === "retail"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-purple-100 text-purple-700 border border-purple-300"
                        }`}
                      >
                        {l.price_type === "retail" ? "R" : "W"}
                      </button>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={l.mrp}
                        placeholder="MRP"
                        onChange={(e) => updateLine(idx, { mrp: parseFloat(e.target.value || "0") })}
                        className="h-8 bg-[#f4f5f7] border-0 focus-visible:ring-1 focus-visible:ring-[#9E6D5B]/40 text-center font-mono-data text-xs rounded-md px-1"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        value={l.line_discount_pct}
                        placeholder="Disc%"
                        onChange={(e) => updateLine(idx, { line_discount_pct: parseFloat(e.target.value || "0") })}
                        className="h-8 bg-[#f4f5f7] border-0 focus-visible:ring-1 focus-visible:ring-[#9E6D5B]/40 text-center font-mono-data text-xs rounded-md px-1"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={l.unit_price}
                        placeholder="Price"
                        onChange={(e) => updateLine(idx, { unit_price: parseFloat(e.target.value || "0") })}
                        className="h-8 bg-[#f4f5f7] border-0 focus-visible:ring-1 focus-visible:ring-[#9E6D5B]/40 text-center font-mono-data text-xs rounded-md px-1"
                      />
                      <Input
                        type="number"
                        min={1}
                        value={l.quantity}
                        placeholder="Qty"
                        onChange={(e) => {
                          const newQty = Math.max(1, parseInt(e.target.value || "1"));
                          if (availableQty != null && newQty > availableQty) {
                            toast.error(`Only ${availableQty} available`);
                            updateLine(idx, { quantity: availableQty > 0 ? availableQty : 1 });
                          } else {
                            updateLine(idx, { quantity: newQty });
                          }
                        }}
                        className={`h-8 bg-[#f4f5f7] border-0 focus-visible:ring-1 focus-visible:ring-[#9E6D5B]/40 text-center font-mono-data text-xs rounded-md px-1 ${
                          availableQty != null && l.quantity > availableQty ? "ring-1 ring-red-500 bg-red-50" : ""
                        }`}
                      />
                    </div>
                  </div>
                  
                  {/* Row 3: Batch info (if applicable) */}
                  {(() => {
                    if (isManual) return null;
                    const batchInfo = stockBatches[l.product_id];
                    if (!batchInfo || !batchInfo.batches?.length) return null;
                    const selected =
                      batchInfo.batches.find((b) => b.id === l.batch_id) || batchInfo.batches[0];
                    const isOpen = !!batchPickerOpen[l.product_id];
                    const others = batchInfo.batches.filter((b) => b.id !== selected?.id);
                    const fmtBatch = (b) => {
                      const mrpLabel =
                        b.mrp != null ? `MRP ₹${Number(b.mrp).toFixed(2).replace(/\.00$/, "")}` : "MRP N/A";
                      let expLabel = "Exp N/A";
                      if (b.expiry_date) {
                        try {
                          const d = new Date(b.expiry_date);
                          if (!isNaN(d)) {
                            expLabel = `Exp ${d.toLocaleString("en-US", { month: "short" })}'${String(
                              d.getFullYear()
                            ).slice(-2)}`;
                          }
                        } catch {
                          /* fallback */
                        }
                      }
                      const status = b.expiry_status;
                      const statusClass =
                        status === "expired"
                          ? "bg-red-100 text-red-700 border-red-300"
                          : status === "expiring_soon"
                          ? "bg-orange-100 text-orange-700 border-orange-300"
                          : status === "expiring_3months"
                          ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                          : "bg-[hsl(var(--muted))] text-muted-foreground border-[hsl(var(--border))]";
                      return { mrpLabel, expLabel, status, statusClass };
                    };
                    const renderChip = (b, { selectedChip }) => {
                      const { mrpLabel, expLabel, status, statusClass } = fmtBatch(b);
                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono-data ${statusClass} ${
                            selectedChip ? "ring-1 ring-[#9E6D5B]/60" : ""
                          }`}
                          title={`${mrpLabel} · ${expLabel} · Qty ${b.quantity}${
                            status === "expired"
                              ? " (expired)"
                              : status === "expiring_soon"
                              ? " (expiring soon)"
                              : ""
                          }`}
                        >
                          <span>{mrpLabel}</span>
                          <span>·</span>
                          <span>{expLabel}</span>
                          <span>·</span>
                          <span className="font-semibold">Qty {b.quantity}</span>
                        </span>
                      );
                    };
                    return (
                      <div
                        className="px-3 pb-2"
                        data-testid={`sale-line-batches-${l.product_id}`}
                      >
                        <div className="flex flex-wrap items-center gap-1">
                          {selected && renderChip(selected, { selectedChip: true })}
                          {staleBatch[l.product_id] && (
                            <span
                              className="inline-flex items-center justify-center px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 text-[10px] font-mono-data"
                              title={
                                staleBatch[l.product_id].reason === "missing"
                                  ? "Original batch from this sale is no longer in stock — earliest-expiry batch auto-selected. Review before saving."
                                  : `Original batch only has ${staleBatch[l.product_id].available} units left (sale line needs ${l.quantity}). Reduce qty or pick another batch.`
                              }
                              data-testid={`sale-line-batch-stale-${l.product_id}`}
                            >
                              ⚠ STOCK MOVED
                            </span>
                          )}
                          {others.length > 0 && (
                            <button
                              type="button"
                              onClick={() =>
                                setBatchPickerOpen({ ...batchPickerOpen, [l.product_id]: !isOpen })
                              }
                              className="text-[10px] text-muted-foreground underline"
                              data-testid={`sale-line-batch-toggle-${l.product_id}`}
                            >
                              {isOpen ? "hide" : `+${others.length} more`}
                            </button>
                          )}
                        </div>
                        {isOpen && others.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            {others.map((b) => {
                              const { mrpLabel, expLabel, status, statusClass } = fmtBatch(b);
                              return (
                                <button
                                  key={b.id}
                                  type="button"
                                  onClick={() => {
                                    if (status === "expired") {
                                      toast.error("Cannot select expired batch");
                                      return;
                                    }
                                    if (l.quantity > b.quantity) {
                                      updateLine(idx, { quantity: Math.max(1, b.quantity) });
                                    }
                                    updateLine(idx, { batch_id: b.id, mrp: Number(b.mrp) });
                                    setBatchPickerOpen({
                                      ...batchPickerOpen,
                                      [l.product_id]: false,
                                    });
                                  }}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono-data cursor-pointer transition-shadow hover:ring-1 hover:ring-[#9E6D5B]/40 ${
                                    status === "expired"
                                      ? "cursor-not-allowed opacity-60"
                                      : ""
                                  } ${statusClass}`}
                                  data-testid={`sale-line-batch-alt-${l.product_id}-${b.id}`}
                                >
                                  <span>{mrpLabel}</span>
                                  <span>·</span>
                                  <span>{expLabel}</span>
                                  <span>·</span>
                                  <span className="font-semibold">Qty {b.quantity}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Search + Camera Scanner - BELOW product list */}
        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Add item</Label>
          <div className="flex gap-2 mt-1">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search by barcode, product, group or brand…"
                className="pl-9"
                data-testid="sale-search-input"
              />
              {results.length > 0 && (
                <div
                  className="absolute z-10 bg-white w-full border border-[hsl(var(--border))] rounded-md mt-1 max-h-72 overflow-y-auto shadow-lg"
                  data-testid="sale-search-results"
                >
                  {results.map((p, i) => {
                    // Stock Lock: check availability
                    const stockAvailable = searchStockMap[p.id];
                    const hasNoStock = stockLockEnabled && (stockAvailable === null || stockAvailable === undefined);
                    const isOutOfStock = stockLockEnabled && stockAvailable !== null && stockAvailable !== undefined && stockAvailable <= 0;
                    const isBlocked = hasNoStock || isOutOfStock;
                    
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => !isBlocked && addLine(p)}
                        disabled={isBlocked}
                        className={`w-full text-left p-2 flex items-center gap-2 ${
                          highlight === i ? "bg-[hsl(var(--muted))]" : ""
                        } ${isBlocked ? "opacity-50 cursor-not-allowed bg-slate-50" : "hover:bg-[hsl(var(--muted))]"}`}
                        data-testid={`sale-search-opt-${p.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-2">
                            {p.product}
                            {isBlocked && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold uppercase shrink-0">
                                Out of Stock
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-mono-data">
                            {p.brand_name} · {p.auto_barcode || "—"}{p.manual_barcode ? ` / ${p.manual_barcode}` : ""}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono-data text-xs font-semibold">
                            ₹{Number(p.mrp || 0).toFixed(2)}
                          </div>
                          {stockLockEnabled && stockAvailable !== null && stockAvailable !== undefined && stockAvailable > 0 && (
                            <div className="text-[10px] text-green-600 font-mono-data">
                              Qty: {stockAvailable}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {searching && !results.length && q.length >= 2 && (
                <div className="text-xs text-muted-foreground mt-1">Searching…</div>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setScannerOpen(true)}
              title="Scan barcode with camera"
              data-testid="sale-scanner-btn"
            >
              <Camera className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setManualItemOpen(true)}
              title="Add a temporary manual item (not tracked in inventory)"
              className="gap-1.5"
              data-testid="sale-add-manual-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline text-xs font-medium">Manual</span>
            </Button>
          </div>
        </div>

        {/* ===== Billing Summary — Compact POS Layout ===== */}

        {/* 1. TOP SUMMARY BAR — single row, 3 equal columns */}
        <BillSummaryBar
          productCount={productCount}
          totalQuantity={totalQuantity}
          totalMRP={totalMRP}
        />

        {/* 2. MAIN GRID — 2 columns: discounts/notes | payments (payments only in edit mode for two-step POS) */}
        <div className={`mt-3 grid gap-3 ${isEditMode ? 'md:grid-cols-2' : ''}`}>
          {/* LEFT: Bill Discount, Flat Total, Notes */}
          <BillDiscountNotes
            billDiscountPct={billDiscountPct}
            setBillDiscountPct={setBillDiscountPct}
            flatTotal={flatTotal}
            setFlatTotal={setFlatTotal}
            totalBeforeRoundoff={totalBeforeRoundoff}
            notes={notes}
            setNotes={setNotes}
          />

          {/* RIGHT: Payments (2x2 grid) - ONLY in Edit Mode for backward compatibility */}
          {isEditMode && (
            <PaymentSection
              cashAmount={cashAmount}
              setCashAmount={setCashAmount}
              upiAmount={upiAmount}
              setUpiAmount={setUpiAmount}
              cardAmount={cardAmount}
              setCardAmount={setCardAmount}
              creditAmount={creditAmount}
              setCreditAmount={setCreditAmount}
              storeCreditAmount={storeCreditAmount}
              setStoreCreditAmount={setStoreCreditAmount}
              availableStoreCredit={availableStoreCredit}
              originalStoreCreditInSale={originalStoreCreditInSale}
              showStoreCredit={!!customer}
              isEditMode={!!editSale}
              outstandingDues={outstandingDues}
              duesCollectionAmount={duesCollectionAmount}
              setDuesCollectionAmount={setDuesCollectionAmount}
              duesCollectionMode={duesCollectionMode}
              setDuesCollectionMode={setDuesCollectionMode}
              billTotal={total}
            />
          )}
        </div>

        {/* Payment validation message / Excess payment handling (ONLY in Edit Mode) */}
        {isEditMode && (() => {
          const paymentsTotal = cashAmount + upiAmount + cardAmount + creditAmount + storeCreditAmount;
          const diff = paymentsTotal - total;
          const excessAmount = diff > 0.01 ? diff : 0;
          const shortfall = diff < -0.01 ? Math.abs(diff) : 0;
          
          // Shortfall - payments less than total
          if (shortfall > 0.01) {
            return (
              <div className="text-xs text-red-600 mt-2" data-testid="sale-payment-mismatch">
                Payments (₹{paymentsTotal.toFixed(2)}) are ₹{shortfall.toFixed(2)} short of total (₹{total.toFixed(2)})
              </div>
            );
          }
          
          // Excess payment - offer to store as credit
          if (excessAmount > 0.01 && customer) {
            return (
              <div className="mt-2 p-2 rounded-md bg-blue-50 border border-blue-200" data-testid="sale-excess-payment">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-blue-800">
                    <span className="font-medium">Excess Payment: ₹{excessAmount.toFixed(2)}</span>
                    <span className="text-blue-600 ml-1">(Bill: ₹{total.toFixed(2)}, Paid: ₹{paymentsTotal.toFixed(2)})</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={storeExcessAsCredit}
                      onChange={(e) => setStoreExcessAsCredit(e.target.checked)}
                      className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                      data-testid="store-excess-checkbox"
                    />
                    <span className="text-xs text-blue-700 font-medium">Store as credit</span>
                  </label>
                </div>
                {storeExcessAsCredit && (
                  <div className="text-[10px] text-blue-600 mt-1">
                    ₹{excessAmount.toFixed(2)} will be added to {customer.name}&apos;s store credit for future use
                  </div>
                )}
              </div>
            );
          }
          
          // Excess without customer - can't store as credit
          if (excessAmount > 0.01 && !customer) {
            return (
              <div className="text-xs text-amber-600 mt-2" data-testid="sale-excess-no-customer">
                Excess payment of ₹{excessAmount.toFixed(2)} — select a customer to store as credit
              </div>
            );
          }
          
          return null;
        })()}

        {/* Reason for edit (only in edit mode) */}
        {isEditMode && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
            <Label className="text-[10px] uppercase tracking-wider text-amber-800">
              Reason for edit *
            </Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="sale-edit-reason">
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DAMAGED PRODUCT">Damaged Product</SelectItem>
                <SelectItem value="LEAKING / SPILLED">Leaking / Spilled</SelectItem>
                <SelectItem value="EXPIRED PRODUCT">Expired Product</SelectItem>
                <SelectItem value="PUMP/APPLICATOR ISSUE">Pump/Applicator Issue</SelectItem>
                <SelectItem value="UNUSUAL SMELL / TEXTURE">Unusual Smell / Texture</SelectItem>
                <SelectItem value="WRONG PRODUCT DELIVERED">Wrong Product Delivered</SelectItem>
                <SelectItem value="WRONG SHADE / VARIANT">Wrong Shade / Variant</SelectItem>
                <SelectItem value="PACKAGE TAMPERED / OPENED">Package Tampered / Opened</SelectItem>
                <SelectItem value="CHANGED MIND">Changed Mind</SelectItem>
                <SelectItem value="FOUND BETTER ALTERNATIVE">Found Better Alternative</SelectItem>
                <SelectItem value="WANT TO EXCHANGE">Want to Exchange</SelectItem>
                <SelectItem value="ADDED ITEM">Added Item</SelectItem>
                <SelectItem value="QUANTITY CORRECTION">Quantity Correction</SelectItem>
                <SelectItem value="BILLING ERROR">Billing Error</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
            {reason === "OTHER" && (
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter custom reason..."
                className="mt-2 text-xs"
                data-testid="sale-edit-reason-custom"
              />
            )}
          </div>
        )}

        {/* 3. TOTAL SECTION — sticky footer card */}
        <div
          className="mt-3 rounded-lg border border-[hsl(var(--border))] bg-white shadow-sm overflow-hidden sticky bottom-0 z-10"
          data-testid="sale-totals-card"
        >
          <div className="px-4 py-2 space-y-1 bg-[#f8f9fb]">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Subtotal</span>
              <span className="font-mono-data text-slate-700">₹{subtotal.toFixed(2)}</span>
            </div>
            {billDiscountPct > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Bill Discount ({billDiscountPct}%)</span>
                <span className="font-mono-data text-red-600">−₹{billDiscAmt.toFixed(2)}</span>
              </div>
            )}
            {effectiveRoundOff !== 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Round Off {flatTotal === 0 ? "(Auto)" : `(to ₹${flatTotal})`}</span>
                <span className="font-mono-data">
                  {effectiveRoundOff > 0 ? "+" : ""}₹{effectiveRoundOff.toFixed(2)}
                </span>
              </div>
            )}
            {/* Show dues being collected */}
            {duesCollectionAmount > 0 && (
              <div className="flex justify-between text-xs border-t border-dashed pt-1 mt-1">
                <span className="text-orange-600 font-medium">+ Dues Collected</span>
                <span className="font-mono-data text-orange-600 font-medium">₹{duesCollectionAmount.toFixed(2)}</span>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center px-4 py-2.5 bg-slate-900 text-white">
            <span className="text-xs uppercase tracking-wider font-medium">Total to Collect</span>
            <span className="font-mono-data text-2xl font-bold tracking-tight" data-testid="sale-total-amount">
              ₹{(total + duesCollectionAmount).toFixed(2)}
            </span>
          </div>
        </div>

        {/* 4. ACTION BUTTONS */}
        <DialogFooter className="mt-3 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={isEditMode ? () => onOpenChange(false) : handleRequestClose}
            data-testid="sale-cancel-btn"
          >
            Cancel
          </Button>
          
          {/* NEW SALE: Two-Step POS - "Proceed to Payment" opens PaymentSummaryDialog */}
          {!isEditMode && (
            <Button
              onClick={proceedToPayment}
              disabled={saving || !lines.length}
              className="text-white hover:opacity-90"
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="sale-proceed-payment-btn"
            >
              Proceed to Payment
            </Button>
          )}
          
          {/* EDIT MODE: Direct submit (backward compatibility) */}
          {isEditMode && (
            <Button
              onClick={submit}
              disabled={saving || !lines.length}
              className="text-white hover:opacity-90"
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="sale-confirm-btn"
            >
              {saving ? "Saving…" : "Update Sale"}
            </Button>
          )}
        </DialogFooter>

        {/* Camera Scanner Dialog — forces back camera on mobile */}
        <BarcodeScannerDialog
          open={scannerOpen}
          onOpenChange={setScannerOpen}
          onDetected={async (barcode) => {
            try {
              const { data } = await api.get("/products", { params: { q: barcode, limit: 1 } });
              if (data.items && data.items.length > 0) {
                addLine(data.items[0]);
                toast.success("Product added!");
              } else {
                toast.error("Product not found");
              }
            } catch (err) {
              toast.error(formatApiError(err));
            }
          }}
        />

        <AddCustomerInline
          open={addCustOpen}
          onOpenChange={setAddCustOpen}
          defaultName={custQ}
          custQ={custQ}
          onAdded={(c) => {
            pickCustomer(c);
            setAddCustOpen(false);
          }}
        />

        {/* Manual Item Dialog — adds temporary line item that bypasses inventory */}
        <ManualItemDialog
          open={manualItemOpen}
          onOpenChange={setManualItemOpen}
          onAdd={addManualLine}
        />
      </DialogContent>
    </Dialog>

    {/* Close Confirmation Dialog */}
    <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
      <DialogContent className="sm:max-w-md" data-testid="sale-close-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Active Sale In Progress
          </DialogTitle>
          <DialogDescription>
            You have an active sale with {lines.length} item{lines.length !== 1 ? "s" : ""} 
            {customer ? ` for ${customer.name}` : ""}.
            {totalItemsCount > 0 && ` (${totalItemsCount} total quantity)`}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="text-sm text-slate-600">
            Are you sure you want to close? All entered data will be lost.
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="outline"
            onClick={confirmClose}
            data-testid="sale-close-confirm-btn"
          >
            Close Sale
          </Button>
          <Button
            onClick={cancelClose}
            style={{ backgroundColor: "#9E6D5B" }}
            className="text-white"
            data-testid="sale-continue-editing-btn"
          >
            Continue Editing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Batch Selection Dialog - for mandatory batch selection in multi-batch products */}
    <BatchSelectionDialog
      open={batchSelectOpen}
      onOpenChange={setBatchSelectOpen}
      product={pendingProduct}
      batches={pendingBatches}
      defaultDiscountPct={DEFAULT_DISCOUNT}
      onConfirm={handleBatchSelectionConfirm}
    />

    {/* Edit Payment Adjustment Dialog - for handling refunds/collections when editing sales */}
    <EditPaymentAdjustmentDialog
      open={paymentAdjustmentOpen}
      onOpenChange={(open) => {
        setPaymentAdjustmentOpen(open);
        if (!open) {
          setPendingEditPayload(null);
        }
      }}
      originalTotal={originalSaleTotal}
      originalPaid={originalPaidAmount}
      newTotal={total}
      originalPayments={editSale?.payments || []}
      customer={customer}
      onConfirm={handlePaymentAdjustment}
      saving={saving}
    />

    {/* TWO-STEP POS CHECKOUT: Payment Summary Dialog (New Sales Only) */}
    {!isEditMode && (
      <PaymentSummaryDialog
        open={paymentSummaryOpen}
        onOpenChange={setPaymentSummaryOpen}
        customer={customer}
        customerName={customerName}
        lines={lines}
        subtotal={subtotal}
        billDiscountPct={billDiscountPct}
        billDiscountAmount={billDiscAmt}
        total={total}
        roundOffAmount={effectiveRoundOff}
        availableStoreCredit={availableStoreCredit}
        outstandingDues={outstandingDues}
        onBack={backToSale}
        onConfirm={confirmPaymentAndComplete}
        saving={saving}
      />
    )}
    </>
  );
}
